import styled from '@emotion/styled'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { MenuItem, Select } from '@mui/material'
import { memo, useMemo } from 'react'
import { Language } from '../../../pure-js/types/Antiloop'
import Box from './Box'
import useAppState from '../hooks/useAppState'
import { getDefaultLanguageCode } from '../hooks/useDefaultLanguage'
import { persistLanguageToLocalStateStorage } from '../libs/languagePersistence'
import { getGrooverText } from '../libs/GrooverTextRepository'

import swedishFlag from '/swedishFlag.svg'
import britishFlag from '/britishFlag.svg'
import norwegianFlag from '/norwegianFlag.svg'

export type PublicLanguageToggleProps = {
  className?: string
  /**
   * Optional callback for syncing URL query params (e.g. ?lang=no).
   * Called after language has been persisted.
   */
  onLanguageChange?: (language: Language) => void
}

const PublicLanguageToggle: React.FC<PublicLanguageToggleProps> = ({ className, onLanguageChange }) => {
  const { state, setState } = useAppState()
  const value = (state.user?.language || getDefaultLanguageCode()) as Language

  const options = useMemo(
    () =>
      [
        {
          language: 'sv' as const,
          label: 'SE',
          flagSrc: swedishFlag,
          ariaLabelKey: 'publicLanguageToggle.button.sv' as const,
          altKey: 'publicLanguageToggle.flagAlt.sv' as const
        },
        {
          language: 'en' as const,
          label: 'EN',
          flagSrc: britishFlag,
          ariaLabelKey: 'publicLanguageToggle.button.en' as const,
          altKey: 'publicLanguageToggle.flagAlt.en' as const
        },
        {
          language: 'no' as const,
          label: 'NO',
          flagSrc: norwegianFlag,
          ariaLabelKey: 'publicLanguageToggle.button.no' as const,
          altKey: 'publicLanguageToggle.flagAlt.no' as const
        }
      ] as const,
    []
  )

  const setLanguage = (language: Language) => {
    persistLanguageToLocalStateStorage(language)

    setState({
      ...state,
      user: {
        ...state.user,
        language
      },
      selectedLanguage: language
    })

    onLanguageChange?.(language)
  }

  const ariaLabel = getGrooverText('publicLanguageToggle.ariaLabel', value)

  return (
    <Container className={className} direction="row" align="center">
      {/* Desktop: pill toggle */}
      <DesktopOnly>
        <Pill aria-label={ariaLabel} role="group">
          {options.map((opt) => (
            <LangButton
              key={opt.language}
              type="button"
              isActive={value === opt.language}
              onClick={() => setLanguage(opt.language)}
              aria-label={getGrooverText(opt.ariaLabelKey, value)}
            >
              <img src={opt.flagSrc} width={22} height={22} alt={getGrooverText(opt.altKey, value)} />
              <LangText>{opt.label}</LangText>
            </LangButton>
          ))}
        </Pill>
      </DesktopOnly>

      {/* Mobile: compact dropdown */}
      <MobileOnly>
        <MobileSelect
          aria-label={ariaLabel}
          value={value}
          onChange={(e) => setLanguage(e.target.value as Language)}
          IconComponent={KeyboardArrowDownIcon}
          MenuProps={{
            PaperProps: { sx: { borderRadius: '12px' } }
          }}
          renderValue={(selected) => {
            const opt = options.find((o) => o.language === selected) || options[0]
            return (
              <MobileValue direction="row" align="center" gap="10px">
                <img src={opt.flagSrc} width={18} height={18} alt={getGrooverText(opt.altKey, value)} />
                <MobileValueText>{opt.label}</MobileValueText>
              </MobileValue>
            )
          }}
        >
          {options.map((opt) => (
            <MenuItem
              key={opt.language}
              value={opt.language}
              aria-label={getGrooverText(opt.ariaLabelKey, value)}
              sx={{ minHeight: 44 }}
            >
              <MobileOption direction="row" align="center" gap="10px">
                <img src={opt.flagSrc} width={18} height={18} alt={getGrooverText(opt.altKey, value)} />
                <span>{opt.label}</span>
              </MobileOption>
            </MenuItem>
          ))}
        </MobileSelect>
      </MobileOnly>
    </Container>
  )
}

const Container = styled(Box)`
  user-select: none;
`

const DesktopOnly = styled.div`
  display: none;

  @media (min-width: 520px) {
    display: block;
  }
`

const MobileOnly = styled.div`
  display: block;

  @media (min-width: 520px) {
    display: none;
  }
`

const Pill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px;
  border-radius: 14px;
  background: rgba(15, 23, 42, 0.06);
  border: 1px solid rgba(15, 23, 42, 0.12);
  backdrop-filter: blur(10px);
`

const LangButton = styled('button', {
  shouldForwardProp: (prop) => prop !== 'isActive'
})<{ isActive: boolean }>`
  appearance: none;
  border: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 12px;
  background: ${({ isActive }) => (isActive ? '#0f172a' : 'transparent')};
  color: ${({ isActive }) => (isActive ? '#ffffff' : 'rgba(15, 23, 42, 0.78)')};
  font-weight: 800;
  min-height: 48px;
  transition:
    background-color 120ms ease,
    color 120ms ease,
    transform 120ms ease;

  &:hover {
    background: ${({ isActive }) => (isActive ? '#0f172a' : 'rgba(15, 23, 42, 0.06)')};
    transform: ${({ isActive }) => (isActive ? 'none' : 'translateY(-1px)')};
  }

  &:focus-visible {
    outline: 2px solid rgba(15, 23, 42, 0.45);
    outline-offset: 2px;
  }
`

const LangText = styled.span`
  font-size: 14px;
  letter-spacing: 0.3px;
`

const MobileSelect = styled(Select)`
  background: rgba(15, 23, 42, 0.06);
  border: 1px solid rgba(15, 23, 42, 0.12);
  border-radius: 12px;
  min-height: 40px;
  min-width: 84px;

  .MuiSelect-select {
    padding: 8px 10px;
    display: flex;
    align-items: center;
  }

  .MuiOutlinedInput-notchedOutline {
    border: none;
  }

  &:focus-within {
    outline: 2px solid rgba(15, 23, 42, 0.45);
    outline-offset: 2px;
  }
`

const MobileValue = styled(Box)`
  padding-right: 2px;
`

const MobileOption = styled(Box)``

const MobileValueText = styled.span`
  font-weight: 800;
  font-size: 13px;
  letter-spacing: 0.2px;
  color: rgba(15, 23, 42, 0.92);
`

export default memo(PublicLanguageToggle)
